import { GeneralTooltip } from '@components/common/GeneralTooltip';
import MaterialIcon from '@components/common/MaterialIcon';
import type { TRole } from '@conversed/shared/src/central/types/role.types';
import useRole from '@hooks/settings/roles/useRole';
import { useTranslation } from 'react-i18next';
import type { TFormattedEmail } from './bulkinvite.type';

type TBulkInviteReviewModalProps = {
  id: string;
  members: TFormattedEmail[];
  onEditRole?: (rowId: number, role: TRole, check: boolean) => void;
};

export const BulkInviteMembersList: React.FC<TBulkInviteReviewModalProps> = ({
  id,
  members,
  onEditRole,
}) => {
  const { t } = useTranslation();
  const { assignableClientRoles } = useRole();

  if (members?.length === 0) {
    return <></>;
  }

  return (
    <div id={id} className='mt-6 overflow-x-auto max-h-80 overflow-y-auto'>
      <table className='min-w-full border-collapse'>
        <thead className='bg-gray-200 text-gray-600 text-xs'>
          <tr className='border-b border-gray-300 '>
            <th className='p-2 text-left font-normal'>
              {t('members-settings.bulk-invite.table.email')}
            </th>
            <th className='p-2 text-left font-normal'>
              {t('members-settings.bulk-invite.table.first-name')}
            </th>
            <th className='p-2 text-left font-normal'>
              {t('members-settings.bulk-invite.table.last-name')}
            </th>
            {assignableClientRoles.map((role) => {
              return (
                <th key={`${role.id}-title`} className='p-2 text-center'>
                  <div className='flex items-center justify-center space-x-2'>
                    <span>{role.title}</span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className='text-xs font-normal'>
          {members.map((e, idx) => {
            return (
              <tr key={idx} className='border-b'>
                <td
                  className={`flex justify-items p-2 border-none ${e.error ? 'text-red-base' : 'text-gray-700'}`}>
                  {e.email}
                  {e.error && (
                    <GeneralTooltip
                      className='pl-2'
                      text={t(e.error)}
                      placement='right'>
                      <MaterialIcon
                        icon='info'
                        classes={['dashboard__metrics-tooltip-icon pl-1']}
                      />
                    </GeneralTooltip>
                  )}
                </td>
                <td className='p-2 text-gray-700'>{e.firstName || '-'}</td>
                <td className='p-2 text-gray-700'>{e.lastName || '-'}</td>
                {assignableClientRoles.map((role) => {
                  return (
                    <td key={`${role.id}-check`} className='p-2 text-center'>
                      <div className='flex justify-center items-center h-full'>
                        <input
                          type='checkbox'
                          readOnly={!!onEditRole}
                          disabled={!!e.error || !onEditRole}
                          checked={e.roles.includes(role.id)}
                          onChange={(ev) =>
                            onEditRole?.(idx, role, ev.target.checked)
                          }
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default BulkInviteMembersList;
